import { useState, type JSX } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  Group,
  List,
  Modal,
  Space,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconTrash } from '@tabler/icons-react';

export function AccountDeletion(): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [deleteText, setDeleteText] = useState<string>('');
  const [understanding, setUnderstanding] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');

  const handleDeleteRequest = async () => {
    if (deleteText !== 'DELETE') {
      notifications.show({
        title: 'Confirmation Text Required',
        message: 'Please type DELETE to confirm account deletion',
        color: 'red',
      });
      return;
    }

    setLoading(true);

    try {
      // This would be the actual API call to delete the account
      // await medplum.executeBot('account-deletion-bot', { reason });
      
      // For demo purposes, simulating a successful deletion
      setTimeout(() => {
        // Clear login session
        medplum.signOut();
        
        // Redirect to a confirmation page
        window.location.href = '/account-deletion-confirmation';
      }, 2000);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'There was a problem deleting your account. Please contact support.',
        color: 'red',
      });
      setLoading(false);
    }
  };

  return (
    <Container size="md">
      <Card withBorder p="xl" radius="md" shadow="sm">
        <Title order={2} mb="md" style={{ color: 'var(--mantine-color-red-7)' }}>
          Delete Your Account
        </Title>
        
        <Text size="sm" mb="lg">
          We're sorry to see you go. Before you proceed with deleting your account, please review the following
          information about what happens to your data.
        </Text>

        <Card withBorder p="md" radius="md" bg="gray.0" mb="lg">
          <Title order={4} mb="sm">What Will Be Deleted:</Title>
          <List size="sm" spacing="xs">
            <List.Item>Your profile information (name, email)</List.Item>
            <List.Item>Your conversation history</List.Item>
            <List.Item>Any questionnaire responses</List.Item>
            <List.Item>Your subscription information and payment methods</List.Item>
          </List>

          <Space h="md" />
          
          <Title order={4} mb="sm">What May Be Retained:</Title>
          <List size="sm" spacing="xs">
            <List.Item>Financial records required by tax laws (typically 7 years)</List.Item>
            <List.Item>De-identified, aggregated analytics data that cannot be traced back to you</List.Item>
            <List.Item>Records needed for security and fraud prevention (for 90 days)</List.Item>
          </List>
        </Card>

        <Title order={4} mb="md">Timeline for Deletion</Title>
        <Text size="sm" mb="xs">• Your account access will be terminated immediately</Text>
        <Text size="sm" mb="xs">• Your personal information will be deleted within 30 days</Text>
        <Text size="sm" mb="lg">• Your data will be removed from backup systems within 90 days</Text>

        <Divider my="lg" />

        <Title order={4} mb="md">Optional: Help Us Improve</Title>
        <Text size="sm" mb="md">
          We'd appreciate knowing why you're leaving. This feedback helps us improve our service.
        </Text>
        <TextInput
          placeholder="Why are you deleting your account? (Optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          mb="lg"
        />

        <Divider my="lg" />

        <Checkbox
          checked={understanding}
          onChange={(e) => setUnderstanding(e.target.checked)}
          label="I understand that deleting my account is permanent and cannot be undone"
          mb="md"
        />

        <Button
          leftSection={<IconTrash size={16} />}
          color="red"
          onClick={() => setConfirmModalOpen(true)}
          disabled={!understanding}
          fullWidth
        >
          Request Account Deletion
        </Button>
      </Card>

      <Modal 
        opened={confirmModalOpen} 
        onClose={() => setConfirmModalOpen(false)}
        title={<Text fw={700} c="red">Final Confirmation</Text>}
        centered
      >
        <Text size="sm" mb="lg">
          This action is permanent. All your personal data will be deleted according to our data retention policy.
        </Text>

        <Text fw={500} mb="xs">To confirm, please type "DELETE" in the field below:</Text>
        <TextInput
          value={deleteText}
          onChange={(e) => setDeleteText(e.target.value)}
          placeholder="Type DELETE"
          mb="md"
        />

        <Group justify="flex-end" gap="md">
          <Button variant="outline" onClick={() => setConfirmModalOpen(false)}>
            Cancel
          </Button>
          <Button 
            color="red" 
            onClick={handleDeleteRequest} 
            loading={loading}
            leftSection={<IconAlertCircle size={16} />}
          >
            Delete My Account
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}